import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { EquipmentFace } from '../../../types';
import type { DeviceModel, ElectricalGraphPinSide } from '../../../types/deviceModel';
import type { FacedPort } from '../../../engine/networkLogic/layoutPorts';
import {
  autoLayoutCards,
  cardIsLive,
  cardSummary,
  connectPin,
  deleteElectricalCard,
  disconnectPin,
  edgesFromModel,
  inputPinId,
  mergeGraphPositions,
  outputPinId,
  parseCardPinId,
  portPinId,
  pruneStaleWireEnds,
  rolePinId,
  snapshotElectricalGraph,
  type GraphEdge,
} from '../../../engine/networkLogic/electricalGraph';
import { EDITOR_CELL_GAP, EDITOR_CELL_SIZE } from '../../../utils/portLayout';
import { flexiControlPoints } from '../../../utils/cableGeometry';
import ElectricalCardNode, {
  CARD_H,
  CARD_H_POLES,
  CARD_HEADER_H,
  CARD_TONES,
  CARD_W,
  toneForPortRole,
  toneForPowerInput,
  toneForPowerOutput,
} from './ElectricalCardNode';

export interface BlueprintBoardCell {
  row: number;
  col: number;
  type: string;
  number?: number;
}

interface LayoutBlueprintCanvasProps {
  draft: DeviceModel;
  onChange: (next: DeviceModel) => void;
  ports: FacedPort[];
  onPortsChange: (next: FacedPort[]) => void;
  activeFace: EquipmentFace;
  showBoard: boolean;
  boardWidth: number;
  boardHeight: number;
  boardCols: number;
  cells: BlueprintBoardCell[];
  board: React.ReactNode;
  selectedCardId: string | null;
  onSelectCard: (id: string | null) => void;
  commitRef?: React.MutableRefObject<(() => DeviceModel) | null>;
}

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2.2;

type DragState =
  | { kind: 'pan'; startX: number; startY: number; origX: number; origY: number }
  | { kind: 'card'; id: string; startX: number; startY: number; origX: number; origY: number }
  | { kind: 'board'; startX: number; startY: number; origX: number; origY: number }
  | { kind: 'wire'; pinId: string; side?: ElectricalGraphPinSide }
  | { kind: 'reattach'; pinId: string; edgeIds: string[] };

function flexiPath(a: { x: number; y: number }, b: { x: number; y: number }): string {
  const { c1, c2 } = flexiControlPoints(a, b);
  return `M ${a.x} ${a.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${b.x} ${b.y}`;
}

function isVacantCell(type: string): boolean {
  return !type || type === 'empty' || type === 'blank';
}

const LayoutBlueprintCanvas: React.FC<LayoutBlueprintCanvasProps> = ({
  draft,
  onChange,
  ports,
  onPortsChange,
  activeFace,
  showBoard,
  boardWidth,
  boardHeight,
  boardCols,
  cells,
  board,
  selectedCardId,
  onSelectCard,
  commitRef,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const previewPathRef = useRef<SVGPathElement>(null);
  const hoverPortRef = useRef<string | null>(null);
  const panRef = useRef({ x: draft.electricalGraph?.camera?.x ?? 80, y: draft.electricalGraph?.camera?.y ?? 40 });
  const zoomRef = useRef(draft.electricalGraph?.camera?.zoom ?? 1);
  const dragRef = useRef<DragState | null>(null);
  const draftRef = useRef(draft);
  const portsRef = useRef(ports);
  const onChangeRef = useRef(onChange);
  const onPortsChangeRef = useRef(onPortsChange);
  const persistCameraRef = useRef<(nextPan: { x: number; y: number }, nextZoom: number) => void>(() => undefined);
  const cellsRef = useRef(cells);
  const activeFaceRef = useRef(activeFace);
  const edgesRef = useRef<GraphEdge[]>([]);
  const liveCardsRef = useRef<Record<string, { x: number; y: number }>>({});
  const liveBoardRef = useRef<{ x: number; y: number } | null>(null);
  const cardsPosRef = useRef<Record<string, { x: number; y: number }>>({});
  const reattachLiveRef = useRef<{ pinId: string; side?: ElectricalGraphPinSide } | null>(null);

  const [zoom, setZoom] = useState(zoomRef.current);
  const [pan, setPan] = useState(panRef.current);
  const [dragKind, setDragKind] = useState<DragState['kind'] | null>(null);
  const [wirePinId, setWirePinId] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<GraphEdge | null>(null);
  const selectedCardIdRef = useRef(selectedCardId);
  const selectedEdgeRef = useRef(selectedEdge);
  selectedCardIdRef.current = selectedCardId;
  selectedEdgeRef.current = selectedEdge;

  draftRef.current = draft;
  portsRef.current = ports;
  onChangeRef.current = onChange;
  onPortsChangeRef.current = onPortsChange;
  zoomRef.current = zoom;
  cellsRef.current = cells;
  activeFaceRef.current = activeFace;

  const cardsPos = useMemo(() => {
    const auto = autoLayoutCards(draft);
    return { ...auto, ...(draft.electricalGraph?.cards || {}) };
  }, [draft]);
  cardsPosRef.current = cardsPos;

  const storedBoard = draft.electricalGraph?.boards?.[activeFace] || { x: 280, y: 60 };
  const boardPos = liveBoardRef.current || storedBoard;

  const edges = useMemo(() => edgesFromModel(draft, ports), [draft, ports]);
  edgesRef.current = edges;

  const persistCamera = useCallback((nextPan: { x: number; y: number }, nextZoom: number) => {
    onChangeRef.current(mergeGraphPositions(draftRef.current, { camera: { x: nextPan.x, y: nextPan.y, zoom: nextZoom } }));
  }, []);
  persistCameraRef.current = persistCamera;

  const commitGraph = useCallback(() => {
    const next = snapshotElectricalGraph(draftRef.current, {
      camera: { x: panRef.current.x, y: panRef.current.y, zoom: zoomRef.current },
      cards: { ...(draftRef.current.electricalGraph?.cards || {}), ...cardsPosRef.current, ...liveCardsRef.current },
      board: liveBoardRef.current,
      face: activeFaceRef.current,
    });
    draftRef.current = next;
    onChangeRef.current(next);
    return next;
  }, []);

  if (commitRef) commitRef.current = commitGraph;

  useEffect(() => {
    return () => {
      if (commitRef && commitRef.current === commitGraph) {
        commitRef.current = null;
      }
    };
  }, [commitGraph, commitRef]);

  const worldFromClient = useCallback((clientX: number, clientY: number) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const z = zoomRef.current;
    return {
      x: (clientX - rect.left - panRef.current.x) / z,
      y: (clientY - rect.top - panRef.current.y) / z,
    };
  }, []);

  const measurePin = useCallback((pinId: string, toward?: { x: number; y: number } | null, preferredSide?: ElectricalGraphPinSide | null): { x: number; y: number; halfW: number; halfH: number; side?: ElectricalGraphPinSide | null } | null => {
    const root = worldRef.current;
    if (!root) return null;
    const nodes = Array.from(root.querySelectorAll(`[data-graph-pin="${CSS.escape(pinId)}"]`)) as HTMLElement[];
    if (nodes.length === 0) return null;
    const z = zoomRef.current;
    const measured = nodes.map((el) => {
      const r = el.getBoundingClientRect();
      const center = worldFromClient(r.left + r.width / 2, r.top + r.height / 2);
      const side = el.getAttribute('data-pin-side') as ElectricalGraphPinSide | null;
      return { ...center, halfW: r.width / 2 / z, halfH: r.height / 2 / z, side, el };
    }).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
    if (measured.length === 0) return null;
    if (preferredSide) {
      const hit = measured.find((p) => p.side === preferredSide);
      if (hit) return hit;
    }
    const sided = measured.filter((p) => p.side);
    const pool = sided.length ? sided : measured;
    if (!toward || pool.length === 1) return pool[0];
    return pool.reduce((best, p) => {
      const d = Math.hypot(p.x - toward.x, p.y - toward.y);
      const bd = Math.hypot(best.x - toward.x, best.y - toward.y);
      return d < bd ? p : best;
    });
  }, [worldFromClient]);

  const fallbackCardPin = useCallback((pinId: string): { x: number; y: number; halfW: number; halfH: number } | null => {
    const parsed = parseCardPinId(pinId);
    if (!parsed?.cardId) return null;
    const pos = liveCardsRef.current[parsed.cardId] || cardsPosRef.current[parsed.cardId] || { x: 40, y: 40 };
    if (parsed.kind === 'input-plus') return { x: pos.x + CARD_W, y: pos.y + 48, halfW: 6, halfH: 6 };
    if (parsed.kind === 'input-minus') return { x: pos.x + CARD_W, y: pos.y + CARD_H_POLES - 14, halfW: 6, halfH: 6 };
    return { x: pos.x + CARD_W, y: pos.y + CARD_HEADER_H + (CARD_H - CARD_HEADER_H) / 2, halfW: 6, halfH: 6 };
  }, []);

  const pinWorld = useCallback((pinId: string, toward?: { x: number; y: number } | null, preferredSide?: ElectricalGraphPinSide | null) => (
    measurePin(pinId, toward, preferredSide) || fallbackCardPin(pinId)
  ), [fallbackCardPin, measurePin]);

  const wireEnds = useCallback((edge: GraphEdge) => {
    const stored = draftRef.current.electricalGraph?.wireEnds?.[edge.id];
    const live = reattachLiveRef.current;
    const prefs = { ...stored };
    if (live?.side) {
      if (edge.from.id === live.pinId) prefs.from = live.side;
      if (edge.to.id === live.pinId) prefs.to = live.side;
    }
    const toRough = pinWorld(edge.to.id, null, prefs?.to);
    const from = pinWorld(edge.from.id, toRough, prefs?.from);
    const to = pinWorld(edge.to.id, from, prefs?.to);
    if (!from || !to) return null;
    return { a: { x: from.x, y: from.y }, b: { x: to.x, y: to.y } };
  }, [pinWorld]);

  const redrawWires = useCallback(() => {
    const root = worldRef.current;
    if (!root) return;
    root.querySelectorAll('[data-graph-pin]').forEach((el) => {
      el.removeAttribute('data-live');
    });
    const markLive = (pinId: string, at: { x: number; y: number }) => {
      const nodes = Array.from(root.querySelectorAll(`[data-graph-pin="${CSS.escape(pinId)}"]`)) as HTMLElement[];
      if (nodes.length === 0) return;
      let best = nodes[0];
      let bestD = Infinity;
      nodes.forEach((el) => {
        const r = el.getBoundingClientRect();
        const c = worldFromClient(r.left + r.width / 2, r.top + r.height / 2);
        const d = Math.hypot(c.x - at.x, c.y - at.y);
        if (d < bestD) {
          best = el;
          bestD = d;
        }
      });
      best.setAttribute('data-live', 'true');
    };
    edgesRef.current.forEach((edge) => {
      const group = root.querySelector(`[data-edge-id="${CSS.escape(edge.id)}"]`);
      if (!group) return;
      const path = group.querySelector('path');
      const dragging = dragRef.current;
      if (dragging?.kind === 'reattach' && dragging.edgeIds.includes(edge.id) && reattachLiveRef.current && !reattachLiveRef.current.side) {
        if (path) path.setAttribute('d', '');
        group.querySelectorAll('circle[data-wire-end]').forEach((c) => c.setAttribute('display', 'none'));
        return;
      }
      const ends = wireEnds(edge);
      if (!ends) return;
      if (path) path.setAttribute('d', flexiPath(ends.a, ends.b));
      const circles = group.querySelectorAll('circle[data-wire-end]');
      circles.forEach((c) => {
        const end = c.getAttribute('data-wire-end') === 'from' ? ends.a : ends.b;
        c.removeAttribute('display');
        c.setAttribute('cx', String(end.x));
        c.setAttribute('cy', String(end.y));
      });
      markLive(edge.from.id, ends.a);
      markLive(edge.to.id, ends.b);
    });
  }, [wireEnds, worldFromClient]);

  const clearHoveredPort = () => {
    const prev = hoverPortRef.current;
    if (!prev) return;
    worldRef.current?.querySelector(`[data-graph-pin="${CSS.escape(prev)}"]`)?.classList.remove('hovered');
    hoverPortRef.current = null;
  };

  const setHoveredPort = (pinId: string | null) => {
    if (hoverPortRef.current === pinId) return;
    clearHoveredPort();
    if (!pinId) return;
    const el = worldRef.current?.querySelector(`[data-graph-pin="${CSS.escape(pinId)}"]`);
    el?.classList.add('hovered');
    hoverPortRef.current = pinId;
  };

  const graphTargetAtClient = useCallback((clientX: number, clientY: number): { id: string; side?: ElectricalGraphPinSide } | null => {
    const stack = document.elementsFromPoint(clientX, clientY);
    for (const node of stack) {
      if (!(node instanceof Element)) continue;
      const pip = node.closest('[data-graph-pin][data-pin-side]');
      if (pip instanceof HTMLElement) {
        const id = pip.getAttribute('data-graph-pin');
        if (id) {
          const side = pip.getAttribute('data-pin-side') as ElectricalGraphPinSide | null;
          return { id, side: side || undefined };
        }
      }
    }
    for (const node of stack) {
      if (!(node instanceof Element)) continue;
      const pin = node.closest('[data-graph-pin]');
      if (!(pin instanceof HTMLElement) || pin.hasAttribute('data-pin-side')) continue;
      const id = pin.getAttribute('data-graph-pin');
      if (id) return { id };
    }
    const w = worldFromClient(clientX, clientY);
    let bestId: string | null = null;
    let bestDist = Infinity;
    let bestSide: ElectricalGraphPinSide | undefined;
    const root = worldRef.current;
    if (root) {
      root.querySelectorAll('[data-graph-pin][data-pin-side]').forEach((node) => {
        const el = node as HTMLElement;
        const id = el.getAttribute('data-graph-pin');
        if (!id) return;
        const r = el.getBoundingClientRect();
        const pad = 10;
        if (clientX < r.left - pad || clientX > r.right + pad || clientY < r.top - pad || clientY > r.bottom + pad) return;
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dist = Math.hypot(clientX - cx, clientY - cy);
        if (dist < bestDist) {
          bestId = id;
          bestDist = dist;
          bestSide = (el.getAttribute('data-pin-side') as ElectricalGraphPinSide) || undefined;
        }
      });
    }
    if (bestId) return { id: bestId, side: bestSide };
    for (const cell of cellsRef.current) {
      if (isVacantCell(String(cell.type)) || typeof cell.number !== 'number') continue;
      const id = portPinId(activeFaceRef.current, cell.number, cell.type);
      const measured = measurePin(id, w);
      if (!measured) continue;
      const dist = Math.hypot(w.x - measured.x, w.y - measured.y);
      const hit = Math.max(measured.halfW, measured.halfH) + 8;
      if (dist <= hit && dist < bestDist) {
        bestId = id;
        bestDist = dist;
        bestSide = measured.side || undefined;
      }
    }
    return bestId ? { id: bestId, side: bestSide } : null;
  }, [measurePin, worldFromClient]);

  const setPreviewPath = useCallback((fromId: string, clientX: number, clientY: number, fromSide?: ElectricalGraphPinSide) => {
    const el = previewPathRef.current;
    if (!el) return;
    const hover = graphTargetAtClient(clientX, clientY);
    setHoveredPort(hover?.id && hover.id !== fromId ? hover.id : null);
    let end = worldFromClient(clientX, clientY);
    if (hover?.id) {
      const to = pinWorld(hover.id, null, hover.side);
      if (to) end = { x: to.x, y: to.y };
    }
    const start = pinWorld(fromId, end, fromSide);
    if (!start) return;
    el.setAttribute('d', flexiPath({ x: start.x, y: start.y }, end));
  }, [graphTargetAtClient, pinWorld, worldFromClient]);

  const endPanClass = () => {
    document.documentElement.classList.remove('blueprint-is-panning');
    if (viewportRef.current) viewportRef.current.style.cursor = 'grab';
  };

  useLayoutEffect(() => {
    redrawWires();
  }, [redrawWires, edges, pan, zoom, cardsPos, boardPos, boardWidth, boardHeight, boardCols]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;
      e.preventDefault();
      const z = zoomRef.current;
      if (current.kind === 'pan') {
        const next = { x: current.origX + (e.clientX - current.startX), y: current.origY + (e.clientY - current.startY) };
        panRef.current = next;
        const world = worldRef.current;
        if (world) {
          world.style.left = `${next.x}px`;
          world.style.top = `${next.y}px`;
        }
        return;
      }
      if (current.kind === 'card') {
        const next = {
          x: current.origX + (e.clientX - current.startX) / z,
          y: current.origY + (e.clientY - current.startY) / z,
        };
        liveCardsRef.current[current.id] = next;
        const el = worldRef.current?.querySelector(`[data-card-id="${CSS.escape(current.id)}"]`) as HTMLElement | null;
        if (el) {
          el.style.left = `${next.x}px`;
          el.style.top = `${next.y}px`;
        }
        redrawWires();
        return;
      }
      if (current.kind === 'board') {
        const next = {
          x: current.origX + (e.clientX - current.startX) / z,
          y: current.origY + (e.clientY - current.startY) / z,
        };
        liveBoardRef.current = next;
        const el = worldRef.current?.querySelector('[data-blueprint-board]') as HTMLElement | null;
        if (el) {
          el.style.left = `${next.x}px`;
          el.style.top = `${next.y}px`;
        }
        redrawWires();
        return;
      }
      if (current.kind === 'wire') {
        setPreviewPath(current.pinId, e.clientX, e.clientY, current.side);
        return;
      }
      if (current.kind === 'reattach') {
        const hover = graphTargetAtClient(e.clientX, e.clientY);
        if (hover?.id === current.pinId && hover.side) {
          reattachLiveRef.current = { pinId: current.pinId, side: hover.side };
          const preview = previewPathRef.current;
          if (preview) preview.setAttribute('d', '');
          setHoveredPort(null);
          redrawWires();
          return;
        }
        reattachLiveRef.current = { pinId: current.pinId };
        const bundle = edgesRef.current.filter((edge) => current.edgeIds.includes(edge.id));
        const sample = bundle[0];
        const otherId = sample
          ? (sample.from.id === current.pinId ? sample.to.id : sample.from.id)
          : current.pinId;
        setPreviewPath(otherId, e.clientX, e.clientY);
        redrawWires();
      }
    };

    const onUp = (e: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;
      dragRef.current = null;
      try {
        viewportRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }

      if (current.kind === 'pan') {
        endPanClass();
        setPan(panRef.current);
        setDragKind(null);
        persistCameraRef.current(panRef.current, zoomRef.current);
        return;
      }
      if (current.kind === 'card') {
        const z = zoomRef.current;
        const next = {
          x: current.origX + (e.clientX - current.startX) / z,
          y: current.origY + (e.clientY - current.startY) / z,
        };
        liveCardsRef.current[current.id] = next;
        const el = worldRef.current?.querySelector(`[data-card-id="${CSS.escape(current.id)}"]`) as HTMLElement | null;
        if (el) el.style.cursor = 'grab';
        onChangeRef.current(mergeGraphPositions(draftRef.current, { cards: { [current.id]: next } }));
        setDragKind(null);
        requestAnimationFrame(() => redrawWires());
        return;
      }
      if (current.kind === 'board') {
        const z = zoomRef.current;
        const next = {
          x: current.origX + (e.clientX - current.startX) / z,
          y: current.origY + (e.clientY - current.startY) / z,
        };
        liveBoardRef.current = next;
        onChangeRef.current(mergeGraphPositions(draftRef.current, { boards: { [activeFaceRef.current]: next } }));
        setDragKind(null);
        requestAnimationFrame(() => redrawWires());
        return;
      }
      const target = graphTargetAtClient(e.clientX, e.clientY);
      clearHoveredPort();
      if (current.kind === 'reattach') {
        reattachLiveRef.current = null;
      }
      if (current.kind === 'wire') {
        if (target && target.id !== current.pinId) {
          const result = connectPin(draftRef.current, portsRef.current, current.pinId, target.id);
          if (result.ok) {
            const fromIsPort = parseCardPinId(current.pinId)?.kind === 'port';
            const edgeId = fromIsPort ? `${target.id}->${current.pinId}` : `${current.pinId}->${target.id}`;
            const withEnds = mergeGraphPositions(result.model, {
              wireEnds: {
                [edgeId]: fromIsPort
                  ? { from: target.side, to: current.side }
                  : { from: current.side, to: target.side },
              },
            });
            onChangeRef.current(pruneStaleWireEnds(withEnds, result.ports));
            onPortsChangeRef.current(result.ports);
          }
        }
      } else if (current.kind === 'reattach' && target) {
        const bundle = edgesRef.current.filter((edge) => current.edgeIds.includes(edge.id));
        if (target.id === current.pinId) {
          if (target.side) {
            const wireEndsPatch: Record<string, { from?: ElectricalGraphPinSide; to?: ElectricalGraphPinSide }> = {};
            bundle.forEach((edge) => {
              wireEndsPatch[edge.id] = edge.from.id === current.pinId
                ? { from: target.side }
                : { to: target.side };
            });
            onChangeRef.current(mergeGraphPositions(draftRef.current, { wireEnds: wireEndsPatch }));
          }
        } else {
          let model = draftRef.current;
          let nextPorts = portsRef.current;
          bundle.forEach((edge) => {
            const otherId = edge.from.id === current.pinId ? edge.to.id : edge.from.id;
            if (otherId === target.id) return;
            const result = disconnectPin(model, nextPorts, edge.from.id, edge.to.id);
            if (!result.ok) return;
            const nextConnect = connectPin(result.model, result.ports, otherId, target.id);
            if (!nextConnect.ok) return;
            const fromIsPort = parseCardPinId(otherId)?.kind === 'port';
            const newId = fromIsPort ? `${target.id}->${otherId}` : `${otherId}->${target.id}`;
            const prevEnds = result.model.electricalGraph?.wireEnds?.[edge.id] || {};
            const cleared = { ...(nextConnect.model.electricalGraph?.wireEnds || {}) };
            delete cleared[edge.id];
            model = mergeGraphPositions(
              { ...nextConnect.model, electricalGraph: { ...nextConnect.model.electricalGraph, wireEnds: cleared } },
              {
                wireEnds: {
                  [newId]: fromIsPort
                    ? { from: target.side, to: prevEnds.to }
                    : { from: prevEnds.from, to: target.side },
                },
              }
            );
            nextPorts = nextConnect.ports;
          });
          onChangeRef.current(pruneStaleWireEnds(model, nextPorts));
          onPortsChangeRef.current(nextPorts);
        }
      }
      setWirePinId(null);
      setDragKind(null);
    };

    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('pointercancel', onUp, true);
    return () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('pointercancel', onUp, true);
    };
  }, [graphTargetAtClient, redrawWires, setPreviewPath]);

  const beginDrag = (next: DragState, pointerId: number) => {
    dragRef.current = next;
    setDragKind(next.kind);
    if (next.kind === 'pan') {
      document.documentElement.classList.add('blueprint-is-panning');
      if (viewportRef.current) viewportRef.current.style.cursor = 'grabbing';
    }
    if (next.kind === 'wire' || next.kind === 'reattach') setWirePinId(next.pinId);
    if (next.kind !== 'wire' && next.kind !== 'reattach') {
      try {
        viewportRef.current?.setPointerCapture(pointerId);
      } catch {
        // optional
      }
    }
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * factor));
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const wx = (cx - pan.x) / zoom;
    const wy = (cy - pan.y) / zoom;
    const nextPan = { x: cx - wx * nextZoom, y: cy - wy * nextZoom };
    panRef.current = nextPan;
    zoomRef.current = nextZoom;
    setPan(nextPan);
    setZoom(nextZoom);
    persistCamera(nextPan, nextZoom);
  };

  const removeCard = useCallback((cardId: string) => {
    const result = deleteElectricalCard(draftRef.current, portsRef.current, cardId);
    if (!result.ok) return;
    delete liveCardsRef.current[cardId];
    onChangeRef.current(pruneStaleWireEnds(result.model, result.ports));
    onPortsChangeRef.current(result.ports);
    onSelectCard(null);
  }, [onSelectCard]);

  const removeEdge = useCallback((edge: GraphEdge) => {
    const result = disconnectPin(draftRef.current, portsRef.current, edge.from.id, edge.to.id);
    if (!result.ok) return;
    const wireEnds = { ...(result.model.electricalGraph?.wireEnds || {}) };
    delete wireEnds[edge.id];
    onChangeRef.current(pruneStaleWireEnds({
      ...result.model,
      electricalGraph: { ...(result.model.electricalGraph || {}), wireEnds },
    }, result.ports));
    onPortsChangeRef.current(result.ports);
    setSelectedEdge(null);
  }, []);

  const startWire = (pinId: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const sideEl = (e.target as HTMLElement).closest('[data-pin-side]');
    const side = (sideEl?.getAttribute('data-pin-side') || (e.currentTarget as HTMLElement).getAttribute('data-pin-side')) as ElectricalGraphPinSide | null;
    beginDrag({ kind: 'wire', pinId, side: side || undefined }, e.pointerId);
    requestAnimationFrame(() => setPreviewPath(pinId, e.clientX, e.clientY, side || undefined));
  };

  const startReattach = (edges: GraphEdge[], pinId: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const sideEl = (e.target as HTMLElement).closest('[data-pin-side]');
    const side = (sideEl?.getAttribute('data-pin-side') || null) as ElectricalGraphPinSide | null;
    reattachLiveRef.current = { pinId, side: side || undefined };
    beginDrag({ kind: 'reattach', pinId, edgeIds: edges.map((edge) => edge.id) }, e.pointerId);
    requestAnimationFrame(() => redrawWires());
  };

  const startPinDrag = (pinId: string, e: React.PointerEvent) => {
    const matches = edgesRef.current.filter((edge) => edge.from.id === pinId || edge.to.id === pinId);
    if (matches.length === 0) {
      startWire(pinId, e);
      return;
    }
    startReattach(matches, pinId, e);
  };

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Delete' && ev.key !== 'Backspace') return;
      const target = ev.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      const edge = selectedEdgeRef.current;
      const cardId = selectedCardIdRef.current;
      if (!edge && !cardId) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (edge) {
        removeEdge(edge);
        return;
      }
      if (cardId) removeCard(cardId);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [removeCard, removeEdge]);

  return (
    <div
      ref={viewportRef}
      className="blueprint-canvas-root flex-1 min-h-0 relative overflow-hidden bg-[#151922]"
      data-routing={dragKind === 'wire' || dragKind === 'reattach' ? 'true' : undefined}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        touchAction: 'none',
        cursor: dragKind === 'pan' ? 'grabbing' : 'grab',
        isolation: 'isolate',
        transform: 'translateZ(0)',
      }}
      onWheel={onWheel}
      onPointerDown={(e) => {
        if (e.button !== 0 && e.button !== 1) return;
        const target = e.target as HTMLElement;
        const interactive = target.closest('[data-blueprint-card], [data-graph-pin], [data-blueprint-board], [data-blueprint-wire], [data-blueprint-port]');
        if (interactive) return;
        e.preventDefault();
        beginDrag({ kind: 'pan', startX: e.clientX, startY: e.clientY, origX: panRef.current.x, origY: panRef.current.y }, e.pointerId);
        onSelectCard(null);
        setSelectedEdge(null);
      }}
    >
      <div
        ref={worldRef}
        className="absolute origin-top-left pointer-events-none"
        style={{
          left: pan.x,
          top: pan.y,
          transform: `translateZ(0) scale(${zoom})`,
          width: 8000,
          height: 8000,
          overflow: 'visible',
        }}
      >
        {showBoard && (
          <div
            data-blueprint-board="true"
            className="absolute pointer-events-auto"
            style={{
              left: boardPos.x,
              top: boardPos.y,
              width: boardWidth,
              height: boardHeight,
              overflow: 'visible',
              zIndex: 1,
              cursor: dragKind === 'board' ? 'grabbing' : 'grab',
            }}
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              if (dragRef.current?.kind === 'wire' || dragRef.current?.kind === 'reattach') return;
              const target = e.target as HTMLElement;
              const pip = target.closest('[data-graph-pin][data-pin-side]') as HTMLElement | null;
              if (pip) {
                const pinId = pip.getAttribute('data-graph-pin');
                if (pinId) startPinDrag(pinId, e);
                return;
              }
              if (target.closest('[data-layout-cell]')) return;
              e.stopPropagation();
              const origin = liveBoardRef.current || storedBoard;
              beginDrag(
                { kind: 'board', startX: e.clientX, startY: e.clientY, origX: origin.x, origY: origin.y },
                e.pointerId
              );
            }}
          >
            {board}
          </div>
        )}

        <svg
          className="blueprint-wire-layer pointer-events-none"
          width={8000}
          height={8000}
          viewBox="0 0 8000 8000"
          overflow="visible"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: 8000,
            height: 8000,
            overflow: 'visible',
            zIndex: 10,
          }}
        >
          {edges.map((edge) => {
            const selected = selectedEdge?.id === edge.id;
            let toneKey = toneForPowerInput('dc-jack');
            if (edge.from.kind === 'role') {
              const role = (draft.portRoles || []).find((r) => r.id === edge.from.cardId);
              toneKey = toneForPortRole(role?.kind || 'poe-out');
            } else if (edge.from.kind === 'output') {
              const output = (draft.powerOutputs || []).find((o) => o.id === edge.from.cardId);
              toneKey = toneForPowerOutput(output?.kind || 'dc');
            } else {
              const input = draft.powerInputs.find((i) => i.id === edge.from.cardId);
              toneKey = input ? toneForPowerInput(input.kind) : toneKey;
            }
            const color = selected ? '#f8d36a' : CARD_TONES[toneKey].wire;
            return (
              <g key={edge.id} data-edge-id={edge.id}>
                <path
                  data-blueprint-wire="true"
                  d=""
                  fill="none"
                  stroke={color}
                  strokeWidth={selected ? 3 : 2.25}
                  className="pointer-events-auto"
                  style={{ pointerEvents: 'stroke' }}
                  onPointerDown={(ev) => {
                    if (ev.button !== 0) return;
                    ev.stopPropagation();
                    setSelectedEdge(edge);
                    onSelectCard(null);
                  }}
                  onContextMenu={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    removeEdge(edge);
                  }}
                />
                {(['from', 'to'] as const).map((end) => (
                  <circle
                    key={end}
                    data-wire-end={end}
                    r={selected ? 6 : 5}
                    fill={color}
                    stroke="rgba(255,255,255,0.9)"
                    strokeWidth={1.25}
                    className="pointer-events-auto"
                    style={{ cursor: 'grab' }}
                    onPointerDown={(ev) => {
                      if (ev.button !== 0) return;
                      startReattach([edge], end === 'from' ? edge.from.id : edge.to.id, ev);
                    }}
                    onContextMenu={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      removeEdge(edge);
                    }}
                  />
                ))}
              </g>
            );
          })}
          {wirePinId && (
            <path
              ref={previewPathRef}
              d=""
              fill="none"
              stroke="#60a5fa"
              strokeWidth={2.25}
              strokeDasharray="6 4"
              style={{ pointerEvents: 'none' }}
            />
          )}
        </svg>

        {draft.powerInputs.filter((input) => {
          if (input.kind !== 'poe-in') return true;
          return !(draft.portRoles || []).some((role) => role.kind === 'poe-in');
        }).map((input) => {
          const pos = liveCardsRef.current[input.id] || cardsPos[input.id] || { x: 40, y: 40 };
          const poles = input.kind === 'pv' || input.kind === 'battery' ? 'plusMinus' : 'single';
          return (
            <ElectricalCardNode
              key={input.id}
              id={input.id}
              title={input.kind === 'poe-in' ? 'PoE Input' : input.kind === 'pv' ? 'PV / MPPT' : input.kind === 'battery' ? 'Battery' : input.kind === 'ac' ? 'AC Input' : 'DC Input'}
              subtitle="Power Input"
              summary={cardSummary(input)}
              tone={toneForPowerInput(input.kind)}
              live={cardIsLive(draft, ports, input.id)}
              selected={selectedCardId === input.id}
              x={pos.x}
              y={pos.y}
              poles={poles}
              pinId={inputPinId(input.id)}
              plusPinId={inputPinId(input.id, 'plus')}
              minusPinId={inputPinId(input.id, 'minus')}
              pinsOpen={dragKind === 'wire' || dragKind === 'reattach' || parseCardPinId(wirePinId || '')?.cardId === input.id}
              onPointerDown={(e) => {
                e.stopPropagation();
                onSelectCard(input.id);
                (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
                beginDrag(
                  { kind: 'card', id: input.id, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y },
                  e.pointerId
                );
              }}
              onPinDown={(pole, e) => startPinDrag(inputPinId(input.id, pole === 'out' ? 'out' : pole), e)}
              onDelete={() => removeCard(input.id)}
            />
          );
        })}
        {(draft.portRoles || []).map((role) => {
          const pos = liveCardsRef.current[role.id] || cardsPos[role.id] || { x: 40, y: 200 };
          return (
            <ElectricalCardNode
              key={role.id}
              id={role.id}
              title={role.name}
              subtitle={role.kind === 'poe-in' ? 'PoE Input' : 'PoE Output'}
              summary={cardSummary(undefined, undefined, role)}
              tone={toneForPortRole(role.kind)}
              live={cardIsLive(draft, ports, role.id)}
              selected={selectedCardId === role.id}
              x={pos.x}
              y={pos.y}
              pinId={rolePinId(role.id)}
              pinsOpen={dragKind === 'wire' || dragKind === 'reattach' || parseCardPinId(wirePinId || '')?.cardId === role.id}
              onPointerDown={(e) => {
                e.stopPropagation();
                onSelectCard(role.id);
                (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
                beginDrag(
                  { kind: 'card', id: role.id, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y },
                  e.pointerId
                );
              }}
              onPinDown={(_pole, e) => startPinDrag(rolePinId(role.id), e)}
              onDelete={() => removeCard(role.id)}
            />
          );
        })}
        {(draft.powerOutputs || []).map((output) => {
          const pos = liveCardsRef.current[output.id] || cardsPos[output.id] || { x: 720, y: 40 };
          return (
            <ElectricalCardNode
              key={output.id}
              id={output.id}
              title={output.kind === 'ac' ? 'AC Output' : 'DC Output'}
              subtitle="Power Output"
              summary={cardSummary(undefined, output)}
              tone={toneForPowerOutput(output.kind)}
              live={cardIsLive(draft, ports, output.id)}
              selected={selectedCardId === output.id}
              x={pos.x}
              y={pos.y}
              pinId={outputPinId(output.id)}
              pinsOpen={dragKind === 'wire' || dragKind === 'reattach' || parseCardPinId(wirePinId || '')?.cardId === output.id}
              onPointerDown={(e) => {
                e.stopPropagation();
                onSelectCard(output.id);
                (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
                beginDrag(
                  { kind: 'card', id: output.id, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y },
                  e.pointerId
                );
              }}
              onPinDown={(_pole, e) => startPinDrag(outputPinId(output.id), e)}
              onDelete={() => removeCard(output.id)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default LayoutBlueprintCanvas;
